/**
 * PaywallModal — surfaced when a free-tier user tries to access a paid
 * feature (locked library card, gated detail page, gated AI feature, etc).
 *
 * Single component, single visual language across the app. Routes the
 * user to `/pricing` for the actual checkout flow.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, Check, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const FEATURE_COPY = {
  library_full: {
    title: 'Unlock the full Library',
    body: 'You\'re on Egnite — the free tier — with 80 fundamentals available. Subscribe to Enferno for the full 300+ exercise catalog plus every tier, variant, and progression.',
  },
  nutrition: {
    title: 'Unlock Nutrition',
    body: 'Macros, micronutrients, the Food Library, and meal logging are part of Enferno. Subscribe to unlock the full nutrition pillar.',
  },
  combatives_detailed: {
    title: 'Unlock the full Combatives journal',
    body: 'Egnite stores 30 days of simple session logs. Enferno keeps your entire history and unlocks the full Combatives journal — built after researching the logs competitive martial artists actually keep. Pre-session intent → technique-by-technique breakdowns with position context → live sparring and round stats → honest post-session reflection → one-click CSV export. Personal Game Maps and position-to-submission decision trees — coming soon.',
  },
  ai_workouts: {
    title: 'Unlock AI workout generation',
    body: 'You\'ve hit the Egnite free-tier limit for AI workout generations this month. Enferno gives you 30 generations and 100 chat messages every month.',
  },
  history_unlimited: {
    title: 'Unlock unlimited history',
    body: 'Egnite shows your last 30 days. Enferno keeps every workout, ride, and session you\'ve ever logged.',
  },
  health_sync: {
    title: 'Unlock wearable sync',
    body: 'Apple Health and Google Fit sync are Enferno features.',
  },
  gps: {
    title: 'Unlock GPS tracking',
    body: 'Live GPS tracking on Endurance is an Enferno feature. Manual entry stays free.',
  },
  default: {
    title: 'This is an Enferno feature',
    body: 'You\'re on the Egnite free tier. Subscribe to unlock the full Enferno experience.',
  },
};

const ENFERNO_PERKS = [
  'Full 300+ exercise Library',
  'Unlimited training history',
  'Nutrition pillar + Food Library',
  'Detailed Combatives journals',
  '30 AI workout generations / mo',
  'Apple Health + Google Fit sync',
];

const PaywallModal = ({ open, onOpenChange, feature = 'default' }) => {
  const copy = FEATURE_COPY[feature] || FEATURE_COPY.default;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary sm:max-w-md"
        data-testid={`paywall-modal-${feature}`}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center">
              <Lock className="w-4 h-4 text-enf-accent-fg" />
            </div>
            <span className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-accent-fg">Enferno feature</span>
          </div>
          <DialogTitle className="font-display text-2xl tracking-wide leading-tight">{copy.title}</DialogTitle>
          <DialogDescription className="text-enf-text-secondary text-sm leading-relaxed pt-1">
            {copy.body}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 my-4">
          {ENFERNO_PERKS.map((perk) => (
            <div key={perk} className="flex items-center gap-2 text-sm text-enf-text-secondary">
              <Check className="w-3.5 h-3.5 text-enf-accent-fg flex-shrink-0" />
              <span>{perk}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/pricing"
            onClick={() => onOpenChange(false)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
            data-testid="paywall-subscribe-cta"
          >
            See pricing <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-2.5 text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors"
            data-testid="paywall-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
